import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * LocationTree — Sprint 3.7.
 *
 * 4-level tree (Concessionaire → Country → Property → Tenant) with
 * checkbox multi-select. Selection is constrained to a single level: the
 * first checked node sets the active level, subsequent checks are only
 * accepted at the same level.
 *
 * Props:
 *   tree         — output of transformLocationTree
 *   selection    — { type, ids: string[] }   controlled state
 *   onChange     — (next) => void            new selection
 */
export default function LocationTree({ tree, selection, onChange }) {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(() => {
        // Auto-expand the root (concessionaire) for visibility.
        const out = {};
        (tree || []).forEach(n => { out[n.id] = true; });
        return out;
    });

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return tree;
        const filterRec = (nodes) => nodes.map(n => {
            if (n.children && n.children.length) {
                const kids = filterRec(n.children);
                if (kids.length) return { ...n, children: kids };
                if (n.text.toLowerCase().includes(q)) return { ...n, children: [] };
                return null;
            }
            return n.text.toLowerCase().includes(q) ? n : null;
        }).filter(Boolean);
        return filterRec(tree || []);
    }, [tree, search]);

    const checkedIds = useMemo(() => new Set(selection?.ids || []), [selection]);
    const activeType = selection?.type || null;

    const onToggle = useCallback((node) => {
        const ids = new Set(checkedIds);
        if (ids.has(node.id)) {
            ids.delete(node.id);
            onChange({
                type: ids.size === 0 ? null : activeType,
                ids: Array.from(ids),
            });
            return;
        }
        // Disallow adding a node from a different level.
        if (activeType && activeType !== node.type) return;
        ids.add(node.id);
        onChange({ type: node.type, ids: Array.from(ids) });
    }, [checkedIds, activeType, onChange]);

    const onExpand = useCallback((id) => {
        setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    return (
        <div>
            <input
                type="text"
                className="v3-anlx-tree-search"
                placeholder={t('Search locations…')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setSearch(''); }}
                aria-label={t('Search locations')}
            />
            {activeType && (
                <div style={{ fontSize: 11, color: 'var(--v3-text-muted)', marginBottom: 6 }}>
                    {t('Selecting at')}: <strong style={{ color: 'var(--v3-text-primary)' }}>{t(activeType)}</strong>
                    <button
                        type="button"
                        onClick={() => onChange({ type: null, ids: [] })}
                        style={{ marginLeft: 8, background: 'transparent', border: 0, color: 'var(--v3-accent)', cursor: 'pointer', fontSize: 11 }}
                    >{t('clear')}</button>
                </div>
            )}
            <ul role="tree" className="v3-anlx-tree">
                {(filtered || []).map(node => (
                    <TreeNode
                        key={node.id}
                        node={node}
                        depth={0}
                        expanded={expanded}
                        onExpand={onExpand}
                        checkedIds={checkedIds}
                        activeType={activeType}
                        onToggle={onToggle}
                    />
                ))}
            </ul>
        </div>
    );
}

function TreeNode({ node, depth, expanded, onExpand, checkedIds, activeType, onToggle }) {
    const { t } = useTranslation();
    const hasChildren = node.children && node.children.length > 0;
    const isOpen = !!expanded[node.id];
    const isChecked = checkedIds.has(node.id);
    const disabled = activeType && activeType !== node.type;

    const handleRowKey = (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            if (!disabled) onToggle(node);
        } else if (e.key === 'ArrowRight' && hasChildren && !isOpen) {
            e.preventDefault();
            onExpand(node.id);
        } else if (e.key === 'ArrowLeft' && hasChildren && isOpen) {
            e.preventDefault();
            onExpand(node.id);
        }
    };

    return (
        <li role="treeitem" aria-expanded={hasChildren ? isOpen : undefined} aria-selected={isChecked}>
            <div
                className={`v3-anlx-tree-row${isChecked ? ' is-checked' : ''}${disabled ? ' is-disabled' : ''}`}
                tabIndex={0}
                onKeyDown={handleRowKey}
                onClick={(e) => {
                    if (e.target.tagName === 'INPUT') return; // checkbox handles itself
                    if (hasChildren) onExpand(node.id);
                }}
                title={disabled ? t('Already selecting at {{type}} level', { type: activeType }) : node.text}
            >
                <span
                    className="v3-anlx-tree-toggle"
                    aria-hidden="true"
                >
                    {hasChildren ? (isOpen ? '▾' : '▸') : ''}
                </span>
                <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={disabled}
                    onChange={() => onToggle(node)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`${isChecked ? t('Deselect') : t('Select')} ${node.text}`}
                />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.text}</span>
                <span style={{ fontSize: 10, color: 'var(--v3-text-dim)' }}>{t(node.type)}</span>
            </div>
            {hasChildren && isOpen && (
                <ul role="group" className="v3-anlx-tree-children">
                    {node.children.map(child => (
                        <TreeNode
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            expanded={expanded}
                            onExpand={onExpand}
                            checkedIds={checkedIds}
                            activeType={activeType}
                            onToggle={onToggle}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}
